import { useState, useMemo } from 'react';

import {
  Button,
  Input,
  Badge,
  Avatar,
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Alert,
  EmptyState,
  PageHeader,
  HelpTip,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  useToast,
} from '@/components/ui';
import { SearchIcon, ClientesIcon } from '@/components/icons';
import { useUsers, useDepartments, useProfiles } from '@/lib/admin-store';
import {
  useAcessosDoUsuario,
  useAcessoRegras,
  useAcessoActions,
  nomeBonito,
  type AcessoTelaItem,
  type AcessoRegra,
  type SujeitoTipo,
} from '@/lib/acesso-store';
import type { User, Department, Profile } from '@/lib/admin-types';
import { OPERATIONAL_PERMISSIONS } from '@/lib/admin-types';
import { cn } from '@/lib/cn';
import { useAbaNaUrl } from '@/lib/use-aba-na-url';

/**
 * Tela admin: Permissões.
 *
 * Lista de pessoas à esquerda + o resultado final à direita.
 *
 * --- PERMISSOES-ACESSO / P4 (2026-07-30) ---
 * A aba principal passou a ser "O que esta pessoa acessa": ela mostra, tela a
 * tela, se a pessoa consegue abrir e DE ONDE vem essa permissão (é admin? veio
 * do departamento? do perfil? é exceção dela?) — e deixa conceder/remover ali
 * mesmo. Fonte: `GET /api/v1/acesso/usuario/:id` (motor único em
 * `server/lib/acesso.ts`, migration 419).
 *
 * As abas "Dashboards" e "Indicadores" foram REMOVIDAS. Elas NÃO salvavam
 * nada: o `PUT /api/v1/users/:id` descarta a chave `permissoes` (o Zod do
 * backend não a conhece) e a lista de indicadores vinha de
 * `MOCK_DASHBOARD_INDICATORS`. Era interface mentindo para o admin — ele
 * marcava, clicava em "Salvar permissões", via sucesso e nada acontecia. O
 * bloco "Copiar as permissões de outra pessoa" e o botão "Salvar permissões"
 * saíram junto, pelo mesmo motivo (só mexiam nesses dois campos fantasmas).
 * Se um dia o controle por dashboard/indicador for pra valer, ele nasce como
 * chave no catálogo `acesso_tela` — com persistência de verdade.
 */
export function PermissionsPage() {
  const users = useUsers();
  const departments = useDepartments();
  const profiles = useProfiles();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(users[0]?.id ?? null);

  const selected = useMemo(
    () => users.find((u) => u.id === selectedId) ?? null,
    [users, selectedId]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(
      (u) => u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  return (
    <section className="min-w-0">
      <PageHeader
        crumb="Configurações · Pessoas"
        title="Permissões"
        subtitle="Escolha uma pessoa e veja, tela a tela, o que ela consegue abrir — e por quê."
      />

      <Alert variant="info" rail className="mb-6">
        <span className="inline-flex flex-wrap items-center gap-1">
          <strong>Como o sistema decide:</strong> vale primeiro a exceção da pessoa, depois o
          departamento, depois o perfil. Administrador abre tudo.
          <HelpTip texto="Se a pessoa tem uma exceção, ela decide sozinha — inclusive para bloquear. Sem exceção, vale o que o departamento dela recebeu. Sem regra de departamento, vale o que o perfil dela recebeu. Quem é administrador abre tudo, tenha regra ou não." />
        </span>
      </Alert>

      {users.length === 0 ? (
        <Card variant="flat">
          <EmptyState
            icon={<ClientesIcon className="w-6 h-6" />}
            title="Nenhum usuário cadastrado"
            description="Cadastre as pessoas em “Usuários e acessos” antes de ajustar permissões."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          {/* Lista de usuários (esquerda) */}
          <Card variant="flat" className="min-w-0 overflow-hidden">
            <div className="p-3 border-b border-line-soft dark:border-line-soft-dark">
              <Input
                placeholder="Buscar usuário..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<SearchIcon className="w-4 h-4" />}
                size="sm"
              />
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filtered.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setSelectedId(u.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 text-left border-b border-line-soft dark:border-line-soft-dark transition-colors',
                    'hover:bg-paper-warm dark:hover:bg-paper-dark-warm',
                    selectedId === u.id && 'bg-red-soft dark:bg-red-soft-dark'
                  )}
                >
                  <Avatar name={u.nome} src={u.fotoUrl} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'text-sm font-medium truncate',
                        selectedId === u.id
                          ? 'text-red-deep dark:text-red-glow'
                          : 'text-ink-1 dark:text-ink-dark-1'
                      )}
                    >
                      {u.nome}
                    </div>
                    <div className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
                      {u.isAdmin ? 'Administrador' : `${u.departamentos.length} departamentos`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Painel (direita) */}
          <div className="min-w-0">
            {selected ? (
              <PermissionsPanel
                key={selected.id}
                user={selected}
                departments={departments}
                profiles={profiles}
              />
            ) : (
              <Card variant="flat">
                <EmptyState
                  title="Selecione um usuário"
                  description="Escolha um usuário na lista à esquerda pra ver o que ele acessa."
                />
              </Card>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================
// PAINEL DA PESSOA
// ============================================================

interface PermissionsPanelProps {
  user: User;
  departments: Department[];
  profiles: Profile[];
}

function PermissionsPanel({ user, departments, profiles }: PermissionsPanelProps) {
  const { data, isLoading } = useAcessosDoUsuario(user.id);
  const { regras } = useAcessoRegras();
  // E: a aba vive na URL. Fica AQUI, e não no `PermissionsPage`, porque é este
  // componente que renderiza o `<Tabs>` — o hook tem de morar onde o valor é
  // consumido, senão o painel não enxerga.
  const { aba: abaPerm, setAba: setAbaPerm } = useAbaNaUrl({
    abas: ['acessos', 'departamentos'] as const,
  });

  const acessos = data?.acessos ?? [];
  const liberadas = acessos.filter((a) => a.permitido).length;

  return (
    <Card variant="flat" className="min-w-0">
      <CardContent className="p-4 md:p-6">
        {/* Header da pessoa */}
        <div className="mb-6 flex items-center gap-3">
          <Avatar name={user.nome} src={user.fotoUrl} size="md" />
          <div className="min-w-0">
            <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1 tracking-tight">
              {user.nome}
            </h2>
            <p className="break-words text-sm text-ink-3 dark:text-ink-dark-3">
              {user.email} {user.isAdmin && '· Administrador'}
            </p>
          </div>
        </div>

        {user.isAdmin && (
          <Alert variant="info" rail className="mb-6">
            Esta pessoa é administradora: ela abre tudo, tenha regra ou não. As regras abaixo só
            passam a valer no dia em que você tirar o acesso de administrador dela.
          </Alert>
        )}

        {/* E: era `defaultValue` (não-controlado) — a aba não tinha endereço
            NEM estado observável. Agora vive na URL como nas outras. */}
        <Tabs value={abaPerm} onValueChange={(v) => setAbaPerm(v as typeof abaPerm)}>
          <TabsList>
            <TabsTrigger value="acessos">
              O que esta pessoa acessa{acessos.length > 0 ? ` (${liberadas} de ${acessos.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="departamentos">
              Departamentos ({user.departamentos.length})
            </TabsTrigger>
          </TabsList>

          {/* ABA: o resultado final (P4) */}
          <TabsContent value="acessos">
            {isLoading ? (
              <p className="py-6 text-sm text-ink-2 dark:text-ink-dark-2">Carregando…</p>
            ) : acessos.length === 0 ? (
              <EmptyState
                title="Nenhuma tela sob controle de permissão"
                description="Quando uma tela passa a ser governada por permissão, ela aparece aqui automaticamente."
              />
            ) : (
              <AcessosPorGrupo
                acessos={acessos}
                regras={regras}
                usuario={{
                  id: user.id,
                  nome: user.nome,
                  perfil: data?.usuario.perfil ?? null,
                  departamentos: data?.usuario.departamentos ?? [],
                }}
                departments={departments}
                profiles={profiles}
              />
            )}
          </TabsContent>

          {/* ABA: Departamentos (só leitura — os papéis vivem em Usuários) */}
          <TabsContent value="departamentos">
            <p className="mb-4 text-sm text-ink-2 dark:text-ink-dark-2">
              Departamentos em que a pessoa está e o papel dela em cada um. Os papéis são
              definidos em &ldquo;Usuários e acessos&rdquo;.
            </p>

            {user.departamentos.length === 0 ? (
              <p className="text-sm italic text-ink-3 dark:text-ink-dark-3">
                Esta pessoa não está em nenhum departamento.
              </p>
            ) : (
              <div className="space-y-2">
                {user.departamentos.map((dr) => {
                  const dept = departments.find((d) => d.slug === dr.departmentSlug);
                  // P4b (AJUSTES-POS-2): nome humano do módulo — nunca o slug
                  // técnico ("/menu/comercial").
                  const nomeModulo = dept?.nome || nomeBonito(dr.departmentSlug);
                  return (
                    <div
                      key={dr.departmentSlug}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line p-3 dark:border-line-dark"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-ink-1 dark:text-ink-dark-1">
                          {nomeModulo}
                        </div>
                        <div className="text-xs text-ink-3 dark:text-ink-dark-3">
                          Acesso ao módulo {nomeModulo}
                        </div>
                      </div>
                      <Badge variant="info" size="sm" className="shrink-0">
                        {dr.role === 'lider' ? 'Líder' : dr.role === 'colaborador' ? 'Colaborador' : 'Visualizador'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="mt-4 text-xs text-ink-3 dark:text-ink-dark-3">
              <strong>O que cada papel pode fazer</strong>{' '}
              ({OPERATIONAL_PERMISSIONS.length}): {OPERATIONAL_PERMISSIONS.map((p) => p.label).join(', ')}.
            </p>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// ============================================================
// O RESULTADO FINAL, POR GRUPO DE TELAS
// ============================================================

interface UsuarioAlvo {
  id: string;
  nome: string;
  perfil: string | null;
  departamentos: string[];
}

/** Um lugar onde a permissão pode ser concedida para alcançar esta pessoa. */
interface AlvoDaRegra {
  tipo: SujeitoTipo;
  id: string;
  rotulo: string;
}

function AcessosPorGrupo({
  acessos,
  regras,
  usuario,
  departments,
  profiles,
}: {
  acessos: AcessoTelaItem[];
  regras: AcessoRegra[];
  usuario: UsuarioAlvo;
  departments: Department[];
  profiles: Profile[];
}) {
  const [chaveAberta, setChaveAberta] = useState<string | null>(null);

  const nomeDept = (slug: string) => departments.find((d) => d.slug === slug)?.nome || nomeBonito(slug);
  const nomePerfil = (slug: string) => profiles.find((p) => p.slug === slug)?.nome || nomeBonito(slug);
  const primeiroNome = usuario.nome.split(' ')[0];

  // Onde dá pra conceder para alcançar ESTA pessoa: ela mesma, os
  // departamentos dela e o perfil dela. Nesta ordem — é a mesma do motor.
  const alvos: AlvoDaRegra[] = [
    { tipo: 'usuario', id: usuario.id, rotulo: `Só para ${primeiroNome}` },
    ...usuario.departamentos.map((slug) => ({
      tipo: 'departamento' as SujeitoTipo,
      id: slug,
      rotulo: `Departamento ${nomeDept(slug)}`,
    })),
    ...(usuario.perfil
      ? [{ tipo: 'perfil' as SujeitoTipo, id: usuario.perfil, rotulo: `Perfil ${nomePerfil(usuario.perfil)}` }]
      : []),
  ];

  const grupos = useMemo(() => {
    const mapa = new Map<string, AcessoTelaItem[]>();
    for (const item of acessos) {
      const lista = mapa.get(item.grupo) ?? [];
      lista.push(item);
      mapa.set(item.grupo, lista);
    }
    return Array.from(mapa.entries());
  }, [acessos]);

  function explicarOrigem(item: AcessoTelaItem): string {
    switch (item.origem) {
      case 'admin':
        return 'Porque é administrador';
      case 'usuario':
        return `Exceção para ${primeiroNome}`;
      case 'departamento': {
        const regra = regras.find(
          (r) =>
            r.chave === item.chave &&
            r.sujeito_tipo === 'departamento' &&
            usuario.departamentos.includes(r.sujeito_id),
        );
        return regra ? `Pelo departamento ${nomeDept(regra.sujeito_id)}` : 'Pelo departamento';
      }
      case 'perfil':
        return usuario.perfil ? `Pelo perfil ${nomePerfil(usuario.perfil)}` : 'Pelo perfil';
      case 'nenhuma':
      default:
        return 'Ninguém concedeu ainda';
    }
  }

  return (
    <div className="space-y-8">
      {grupos.map(([grupo, itens]) => (
        <div key={grupo} className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold text-ink-1 dark:text-ink-dark-1">{grupo}</h3>
          <div className="rounded-xl border border-line dark:border-line-dark overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tela</TableHead>
                  <TableHead>Consegue abrir?</TableHead>
                  <TableHead>De onde vem</TableHead>
                  <TableHead align="right">Ajustar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((item) => {
                  const aberta = chaveAberta === item.chave;
                  return [
                    <TableRow key={item.chave}>
                      <TableCell>
                        <div className="font-medium text-ink-1 dark:text-ink-dark-1">{item.nome}</div>
                        <div className="text-xs text-ink-3 dark:text-ink-dark-3">
                          {item.natureza === 'agir' ? 'Permite alterar coisas' : 'Só consultar'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.permitido ? (
                          <Badge variant="success" size="sm" dot>
                            Sim
                          </Badge>
                        ) : (
                          <Badge variant="neutral" size="sm" dot>
                            Não
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-ink-2 dark:text-ink-dark-2">
                          {explicarOrigem(item)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setChaveAberta(aberta ? null : item.chave)}
                          aria-expanded={aberta}
                        >
                          {aberta ? 'Fechar' : 'Ajustar'}
                        </Button>
                      </TableCell>
                    </TableRow>,
                    aberta ? (
                      <TableRow key={`${item.chave}-ajuste`}>
                        <TableCell colSpan={4} className="bg-paper-warm dark:bg-paper-dark-warm">
                          <AjusteDaChave
                            chave={item.chave}
                            nome={item.nome}
                            alvos={alvos}
                            regras={regras}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null,
                  ];
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Conceder/remover a chave para: só esta pessoa, o departamento dela ou o
 * perfil dela. É o mesmo POST/DELETE de `/api/v1/acesso/regras` — a lista
 * recarrega sozinha depois (invalidação no store).
 */
function AjusteDaChave({
  chave,
  nome,
  alvos,
  regras,
}: {
  chave: string;
  nome: string;
  alvos: AlvoDaRegra[];
  regras: AcessoRegra[];
}) {
  const { conceder, remover, pending } = useAcessoActions();
  const { show } = useToast();

  async function aoConceder(alvo: AlvoDaRegra, permitido: boolean) {
    try {
      await conceder({ chave, sujeitoTipo: alvo.tipo, sujeitoId: alvo.id, permitido });
      show(
        permitido
          ? `“${nome}” liberado — ${alvo.rotulo.toLowerCase()}.`
          : `“${nome}” bloqueado — ${alvo.rotulo.toLowerCase()}.`,
        { variant: 'success' },
      );
    } catch {
      show('Não deu pra salvar. Tente de novo.', { variant: 'error' });
    }
  }

  async function aoRemover(regraId: string) {
    try {
      await remover(regraId);
      show('Regra removida. Volta a valer o nível mais geral.', { variant: 'success' });
    } catch {
      show('Não deu pra remover. Tente de novo.', { variant: 'error' });
    }
  }

  return (
    <div className="space-y-3 py-1">
      <p className="text-xs text-ink-3 dark:text-ink-dark-3">
        Quem você quer alcançar? A regra mais específica vence: a da pessoa passa na frente da do
        departamento, que passa na frente da do perfil.
      </p>
      <div className="space-y-2">
        {alvos.map((alvo) => {
          const regra = regras.find(
            (r) => r.chave === chave && r.sujeito_tipo === alvo.tipo && r.sujeito_id === alvo.id,
          );
          const marca = `${chave}:${alvo.tipo}:${alvo.id}`;
          const ocupado = pending === marca || (regra != null && pending === regra.id);
          return (
            <div
              key={`${alvo.tipo}:${alvo.id}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line-soft bg-paper dark:border-line-soft-dark dark:bg-paper-dark-surface px-3 py-2"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-sm text-ink-1 dark:text-ink-dark-1">{alvo.rotulo}</span>
                {regra ? (
                  <Badge variant={regra.permitido ? 'success' : 'danger'} size="sm">
                    {regra.permitido ? 'Pode abrir' : 'Bloqueado'}
                  </Badge>
                ) : (
                  <Badge variant="neutral" size="sm">
                    Sem regra
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {regra ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={ocupado}
                    onClick={() => aoRemover(regra.id)}
                  >
                    Remover regra
                  </Button>
                ) : (
                  <>
                    <Button size="sm" loading={ocupado} onClick={() => aoConceder(alvo, true)}>
                      Liberar
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={ocupado}
                      onClick={() => aoConceder(alvo, false)}
                    >
                      Bloquear
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
